/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import firebase from "firebase";
import { Alert, Divider, Layout, Row, Spin } from "antd";
import { db_user } from "../../components/firestore_user";
import { getDistributionTransactions } from "../../api/distributionTransactions";
import { getDistributionsByUser } from "../../api/distributionProject";
import { useDashboardScope } from "../../hooks/useDashboardScope";
import { netPayoutFromRevenue } from "../../components/utils/youtubePlatform";

const { Content } = Layout;

import Submit from "../../components/submit";
import Projects from "./projects";
import Sales from "./sales";
import Payout from "./payout";

/** Merge Firestore project rows with SQL `distributions` so the portal list is stable when one source lags or is empty. */
function mergeFirestoreAndSql(firestoreRows, sqlRows) {
  const byUid = new Map();
  for (const p of firestoreRows) {
    byUid.set(p.uid, { ...p });
  }
  for (const d of sqlRows) {
    const id = d.project_id;
    if (!id) continue;
    if (!byUid.has(id)) {
      byUid.set(id, {
        uid: id,
        title: d.title,
        distributionStatus: d.status,
        status: "published",
        country: "in",
        rating: 6.5,
      });
    } else {
      const existing = byUid.get(id);
      byUid.set(id, {
        ...existing,
        title: existing.title || d.title,
        distributionStatus: d.status ?? existing.distributionStatus,
      });
    }
  }
  return Array.from(byUid.values());
}

const Common = ({ auth_user }) => {
  const {
    effectiveUid,
    waitingForScope,
    isImpersonating,
    queryUserId,
    linkSearch,
  } = useDashboardScope(auth_user);

  const [projects, updateLeads] = useState([]);
  const [payoutsProject, updatePayoutsProject] = useState([]);
  const [loading, toggleLoading] = useState(true);
  const [revenue, updateRevenue] = useState(0);
  const [netPayout, updateNetPayout] = useState(0);

  const fetchFirestoreProjects = useCallback(async () => {
    if (!effectiveUid) return [];
    try {
      const query = db_user
        .collection("projects")
        .where("username", "==", effectiveUid);
      const querySnapshot = await query.get();
      let arrOfObj = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        uid: doc.id,
      }));
      return arrOfObj.filter(
        (item) => item.status == "published" && item.country == "in"
      );
    } catch (error) {
      console.log(error);
      return [];
    }
  }, [effectiveUid]);

  const track = async () => {
    if (!effectiveUid) return;
    await db_user
      .collection("trackers")
      .doc("distribution")
      .update({
        users: firebase.firestore.FieldValue.arrayUnion(effectiveUid),
      });
  };

  useEffect(() => {
    if (waitingForScope) {
      toggleLoading(true);
      return;
    }
    if (!effectiveUid) {
      toggleLoading(false);
      updateLeads([]);
      updatePayoutsProject({ docs: [] });
      updateRevenue(0);
      updateNetPayout(0);
      return;
    }

    const ac = new AbortController();
    const { signal } = ac;

    const run = async () => {
      toggleLoading(true);

      try {
        const [firestoreProjects, sqlDistributions] = await Promise.all([
          fetchFirestoreProjects(),
          getDistributionsByUser(effectiveUid),
        ]);
        if (signal.aborted) return;

        const merged = mergeFirestoreAndSql(
          firestoreProjects,
          sqlDistributions
        );
        updateLeads(merged);

        const docs = sqlDistributions.map((d) => ({
          id: d.project_id,
          data: () => ({ title: d.title }),
        }));
        updatePayoutsProject({ docs });

        const projectIds = sqlDistributions
          .map((d) => d.project_id)
          .filter(Boolean);
        let total = 0;
        let payoutTotal = 0;
        if (projectIds.length > 0) {
          const transactions = await getDistributionTransactions(projectIds);
          if (signal.aborted) return;
          transactions.forEach((row) => {
            const rev = Number(row.revenue || 0);
            total += rev;
            payoutTotal += netPayoutFromRevenue(
              rev,
              row.platform_id,
              row.platform_display_name
            );
          });
        }
        updateRevenue(total);
        updateNetPayout(payoutTotal);

        if (total > 0 && !isImpersonating) {
          await db_user
            .collection("trackers")
            .doc("distribution")
            .update({
              revenue: firebase.firestore.FieldValue.arrayUnion({
                user: effectiveUid,
                revenue: total / 0.9,
              }),
            });
        }

        if (!isImpersonating) {
          track().catch((err) => {
            console.warn("distribution tracker update failed", err);
          });
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error(error);
          updateLeads([]);
          updateRevenue(0);
          updateNetPayout(0);
          updatePayoutsProject({ docs: [] });
        }
      } finally {
        if (!signal.aborted) toggleLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [effectiveUid, fetchFirestoreProjects, waitingForScope, isImpersonating]);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Layout>
        <Layout style={{ padding: "0 24px 24px" }}>
          <Content
            className="site-layout-background"
            style={{
              marginTop: 24,
              minHeight: 280,
            }}
          >
            {isImpersonating && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Admin view"
                description={`Showing dashboard data for user_id ${queryUserId}.`}
              />
            )}
            {loading ? (
              <div className="vc-spin-wrap">
                <Spin />
              </div>
            ) : (
              <Row>
                <Submit />
                <Divider />
                <Projects projects={projects} linkSearch={linkSearch} />
              </Row>
            )}
            {projects.filter(
              (item) => item.distributionStatus == "live" || item.rating >= 6.5
            ).length > 0 && (
              <>
                <Sales revenue={revenue} netPayout={netPayout} />
                <Divider />
                <Payout
                  effectiveUid={effectiveUid}
                  projects={payoutsProject && payoutsProject.docs}
                />
                <div style={{ marginTop: 16 }}>
                  <Link to={{ pathname: "/payouts", search: linkSearch }}>
                    View revenue details
                  </Link>
                </div>
              </>
            )}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};
export default Common;
